import { useState, useEffect, useCallback } from 'react';
import { get, post, put, del } from '../api';

function Btn({ onClick, children, variant = 'primary', small, disabled, style = {} }) {
  const s = { primary:{background:'#c9a96e',color:'#1a1208',border:'none'}, secondary:{background:'transparent',color:'#8a7055',border:'1px solid #3a2a12'}, danger:{background:'#e63c3c22',color:'#e63c3c',border:'1px solid #e63c3c44'}, success:{background:'#10b98122',color:'#10b981',border:'1px solid #10b98144'}, ghost:{background:'#c9a96e18',color:'#c9a96e',border:'1px solid #c9a96e44'} };
  return <button onClick={onClick} disabled={disabled} style={{...s[variant],borderRadius:8,padding:small?'6px 14px':'10px 22px',fontWeight:700,cursor:disabled?'default':'pointer',fontSize:small?12:14,opacity:disabled?0.5:1,fontFamily:'inherit',transition:'all 0.15s',...style}}>{children}</button>;
}

function Input({ label, value, onChange, placeholder, type='text', rows }) {
  const s = {width:'100%',background:'#2a1f0f',border:'1px solid #3a2a12',borderRadius:8,padding:'10px 14px',color:'#f5e6c8',fontSize:14,boxSizing:'border-box',fontFamily:'inherit'};
  return (
    <div style={{marginBottom:14}}>
      {label && <label style={{display:'block',color:'#8a7055',fontSize:11,marginBottom:6,textTransform:'uppercase',letterSpacing:'0.06em'}}>{label}</label>}
      {rows ? <textarea value={value} onChange={onChange} placeholder={placeholder} rows={rows} style={{...s,resize:'vertical'}} /> : <input type={type} value={value} onChange={onChange} placeholder={placeholder} style={s} />}
    </div>
  );
}

function Modal({ children, onClose, wide }) {
  return (
    <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.8)',zIndex:1000,display:'flex',alignItems:'center',justifyContent:'center',padding:20}} onClick={onClose}>
      <div style={{background:'#1a1208',border:'1px solid #3a2a12',borderRadius:14,padding:32,maxWidth:wide?860:660,width:'100%',maxHeight:'90vh',overflowY:'auto'}} onClick={e=>e.stopPropagation()}>{children}</div>
    </div>
  );
}

function getComplianceStatus(record, type) {
  if (!record) return 'missing';
  if (record.status === 'pending' || record.status === 'missing') return 'missing';
  if (!type.expiry_days) return 'complete';
  if (!record.expiry_date) return 'complete';
  const expiry = new Date(record.expiry_date);
  const now = new Date();
  const daysLeft = Math.floor((expiry - now) / (24*60*60*1000));
  if (daysLeft < 0) return 'expired';
  if (daysLeft <= type.alert_days) return 'expiring';
  return 'complete';
}

const statusColor = { complete:'#10b981', expiring:'#f59e0b', expired:'#e63c3c', missing:'#6b7280' };
const statusLabel = { complete:'✓ Valid', expiring:'⚠ Expiring Soon', expired:'✗ Expired', missing:'— Not on File' };

export function ComplianceTracker({ users, isOwner }) {
  const [types, setTypes] = useState([]);
  const [records, setRecords] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [editModal, setEditModal] = useState(null);
  const [addTypeModal, setAddTypeModal] = useState(false);
  const [form, setForm] = useState({ completed_date:'', expiry_date:'', certificate_url:'', notes:'', status:'complete' });
  const [typeForm, setTypeForm] = useState({ title:'', description:'', expiry_days:730, alert_days:60 });
  const [loading, setLoading] = useState(true);
  const [certExpiry, setCertExpiry] = useState(null);

  const staff = (users||[]).filter(u => u.role==='staff' && u.active);

  const load = useCallback(async () => {
    try {
      const [t, r] = await Promise.all([get('/compliance/types'), get('/compliance/staff')]);
      setTypes(t); setRecords(r);
    } catch(e) {} finally { setLoading(false); }
    try { const ce = await get('/certs/expiry'); setCertExpiry(ce); } catch(e) {}
  }, []);

  useEffect(() => { load(); }, [load]);

  function getRecord(userId, typeId) { return records.find(r => r.user_id===userId && r.compliance_type_id===typeId); }

  function getOverallStatus(userId) {
    const statuses = types.map(t => getComplianceStatus(getRecord(userId, t.id), t));
    if (statuses.includes('expired')) return 'expired';
    if (statuses.includes('missing')) return 'missing';
    if (statuses.includes('expiring')) return 'expiring';
    return 'complete';
  }

  async function saveRecord() {
    await post('/compliance/record', { user_id: editModal.userId, compliance_type_id: editModal.typeId, ...form });
    setEditModal(null);
    load();
  }

  async function addType() {
    await post('/compliance/types', typeForm);
    setAddTypeModal(false);
    setTypeForm({ title:'', description:'', expiry_days:730, alert_days:60 });
    load();
  }

  function openEdit(userId, type) {
    const rec = getRecord(userId, type.id);
    setForm({ completed_date: rec?.completed_date||'', expiry_date: rec?.expiry_date||'', certificate_url: rec?.certificate_url||'', notes: rec?.notes||'', status: rec?.status||'complete' });
    setEditModal({ userId, typeId: type.id, typeName: type.title, userName: staff.find(u=>u.id===userId)?.name||'' });
  }

  const expiringSoon = records.filter(r => { const t = types.find(t=>t.id===r.compliance_type_id); return t && getComplianceStatus(r,t)==='expiring'; });
  const expired = records.filter(r => { const t = types.find(t=>t.id===r.compliance_type_id); return t && getComplianceStatus(r,t)==='expired'; });

  if (loading) return <p style={{color:'#5a4030',textAlign:'center',padding:40}}>Loading...</p>;

  return (
    <div>
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:24}}>
        <div>
          <h2 style={{fontFamily:"'Playfair Display', serif",fontSize:26,color:'#f5e6c8',marginBottom:4}}>Compliance Tracker</h2>
          <p style={{color:'#8a7055',fontSize:13}}>Track certifications, training, and regulatory requirements</p>
        </div>
        <div style={{display:'flex',gap:10}}>
          <a href={`/api/export/compliance?token=${localStorage.getItem('tnt_token')}`} target="_blank" style={{background:'#6366f122',color:'#6366f1',border:'1px solid #6366f144',borderRadius:8,padding:'8px 16px',textDecoration:'none',fontSize:12,fontWeight:700}}>📊 Export CSV</a>
          {isOwner && <Btn onClick={()=>setAddTypeModal(true)}>+ Add Requirement</Btn>}
        </div>
      </div>

      {certExpiry && (
        <div style={{ marginBottom: 28 }}>
          <div style={{ display: 'flex', gap: 12, marginBottom: 14, flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 140, background: certExpiry.summary.expired > 0 ? '#e63c3c18' : '#1a1208', border: `1px solid ${certExpiry.summary.expired > 0 ? '#e63c3c44' : '#3a2a12'}`, borderRadius: 10, padding: '14px 18px' }}>
              <div style={{ fontSize: 26, fontWeight: 800, color: certExpiry.summary.expired > 0 ? '#e63c3c' : '#8a7055' }}>{certExpiry.summary.expired}</div>
              <div style={{ fontSize: 12, color: '#8a7055' }}>Expired</div>
            </div>
            <div style={{ flex: 1, minWidth: 140, background: certExpiry.summary.soon > 0 ? '#f59e0b18' : '#1a1208', border: `1px solid ${certExpiry.summary.soon > 0 ? '#f59e0b44' : '#3a2a12'}`, borderRadius: 10, padding: '14px 18px' }}>
              <div style={{ fontSize: 26, fontWeight: 800, color: certExpiry.summary.soon > 0 ? '#f59e0b' : '#8a7055' }}>{certExpiry.summary.soon}</div>
              <div style={{ fontSize: 12, color: '#8a7055' }}>Expiring soon (60 days)</div>
            </div>
            <div style={{ flex: 1, minWidth: 140, background: certExpiry.summary.missing_required > 0 ? '#e63c3c18' : '#1a1208', border: `1px solid ${certExpiry.summary.missing_required > 0 ? '#e63c3c44' : '#3a2a12'}`, borderRadius: 10, padding: '14px 18px' }}>
              <div style={{ fontSize: 26, fontWeight: 800, color: certExpiry.summary.missing_required > 0 ? '#e63c3c' : '#8a7055' }}>{certExpiry.summary.missing_required}</div>
              <div style={{ fontSize: 12, color: '#8a7055' }}>Missing (required)</div>
            </div>
          </div>

          {(() => {
            const flagged = certExpiry.rows.filter(r => r.status === 'expired' || r.status === 'soon' || (r.status === 'missing' && r.required));
            if (flagged.length === 0) return <div style={{ background: '#10b98112', border: '1px solid #10b98133', borderRadius: 10, padding: 16, color: '#10b981', fontSize: 13 }}>✓ All staff certifications are current. Nothing expiring within 60 days.</div>;
            const color = { expired: '#e63c3c', soon: '#f59e0b', missing: '#e63c3c' };
            const labelFor = (r) => r.status === 'expired' ? `Expired ${Math.abs(r.days)}d ago` : r.status === 'soon' ? `${r.days}d left` : 'Missing';
            return (
              <div style={{ background: '#1a1208', border: '1px solid #3a2a12', borderRadius: 10, overflow: 'hidden' }}>
                <div style={{ padding: '10px 16px', borderBottom: '1px solid #3a2a12', color: '#c9a96e', fontWeight: 700, fontSize: 13 }}>⚠ Certifications needing attention</div>
                {flagged.map((r, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 16px', borderBottom: i < flagged.length - 1 ? '1px solid #2a1f0f' : 'none' }}>
                    <div>
                      <span style={{ color: '#f5e6c8', fontWeight: 600 }}>{r.name}</span>
                      <span style={{ color: '#8a7055', fontSize: 12, marginLeft: 8 }}>{r.cert}{r.position ? ` · ${r.position}` : ''}</span>
                    </div>
                    <span style={{ color: color[r.status], fontSize: 12, fontWeight: 700 }}>{labelFor(r)}</span>
                  </div>
                ))}
              </div>
            );
          })()}
        </div>
      )}

      {(expiringSoon.length > 0 || expired.length > 0) && (
        <div style={{background:'#e63c3c18',border:'1px solid #e63c3c44',borderRadius:10,padding:16,marginBottom:24}}>
          <div style={{color:'#e63c3c',fontWeight:700,marginBottom:8}}>⚠️ Action Required</div>
          {expired.length > 0 && <div style={{color:'#d4b896',fontSize:13,marginBottom:4}}>{expired.length} certification{expired.length!==1?'s':''} expired and need renewal</div>}
          {expiringSoon.length > 0 && <div style={{color:'#d4b896',fontSize:13}}>{expiringSoon.length} certification{expiringSoon.length!==1?'s':''} expiring within alert period</div>}
        </div>
      )}

      {addTypeModal && isOwner && (
        <Modal onClose={()=>setAddTypeModal(false)}>
          <h3 style={{fontFamily:"'Playfair Display', serif",color:'#f5e6c8',marginBottom:20}}>Add Compliance Requirement</h3>
          <Input label="Title" value={typeForm.title} onChange={e=>setTypeForm(p=>({...p,title:e.target.value}))} placeholder="e.g. Food Handler Card" />
          <Input label="Description" value={typeForm.description} onChange={e=>setTypeForm(p=>({...p,description:e.target.value}))} placeholder="Brief description of the requirement..." rows={2} />
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:14,marginBottom:20}}>
            <div>
              <label style={{display:'block',color:'#8a7055',fontSize:11,marginBottom:6,textTransform:'uppercase',letterSpacing:'0.06em'}}>Expires After (days)</label>
              <input type="number" value={typeForm.expiry_days} onChange={e=>setTypeForm(p=>({...p,expiry_days:Number(e.target.value)}))} style={{width:'100%',background:'#2a1f0f',border:'1px solid #3a2a12',borderRadius:8,padding:'10px 14px',color:'#f5e6c8',fontSize:14,boxSizing:'border-box'}} />
              <div style={{color:'#5a4030',fontSize:11,marginTop:4}}>0 = no expiry</div>
            </div>
            <div>
              <label style={{display:'block',color:'#8a7055',fontSize:11,marginBottom:6,textTransform:'uppercase',letterSpacing:'0.06em'}}>Alert Before Expiry (days)</label>
              <input type="number" value={typeForm.alert_days} onChange={e=>setTypeForm(p=>({...p,alert_days:Number(e.target.value)}))} style={{width:'100%',background:'#2a1f0f',border:'1px solid #3a2a12',borderRadius:8,padding:'10px 14px',color:'#f5e6c8',fontSize:14,boxSizing:'border-box'}} />
            </div>
          </div>
          <div style={{display:'flex',gap:10}}>
            <Btn onClick={addType} disabled={!typeForm.title}>Add Requirement</Btn>
            <Btn variant="secondary" onClick={()=>setAddTypeModal(false)}>Cancel</Btn>
          </div>
        </Modal>
      )}

      {editModal && (
        <Modal onClose={()=>setEditModal(null)}>
          <h3 style={{fontFamily:"'Playfair Display', serif",color:'#f5e6c8',marginBottom:6}}>Update Compliance Record</h3>
          <p style={{color:'#8a7055',marginBottom:20}}>{editModal.userName} — {editModal.typeName}</p>
          <div style={{marginBottom:14}}>
            <label style={{display:'block',color:'#8a7055',fontSize:11,marginBottom:8,textTransform:'uppercase',letterSpacing:'0.06em'}}>Status</label>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
              {[{v:'complete',l:'✓ Complete'},{v:'pending',l:'⏳ Pending'}].map(o=>(
                <div key={o.v} onClick={()=>setForm(p=>({...p,status:o.v}))} style={{padding:10,borderRadius:8,cursor:'pointer',border:`1px solid ${form.status===o.v?'#10b981':'#3a2a12'}`,background:form.status===o.v?'#10b98118':'#2a1f0f',color:form.status===o.v?'#10b981':'#8a7055',textAlign:'center',fontSize:13}}>{o.l}</div>
              ))}
            </div>
          </div>
          <Input label="Completion Date" type="date" value={form.completed_date} onChange={e=>setForm(p=>({...p,completed_date:e.target.value}))} />
          <Input label="Expiry Date (if applicable)" type="date" value={form.expiry_date} onChange={e=>setForm(p=>({...p,expiry_date:e.target.value}))} />
          <Input label="Certificate URL (OneDrive or Google Drive link)" value={form.certificate_url} onChange={e=>setForm(p=>({...p,certificate_url:e.target.value}))} placeholder="https://..." />
          <Input label="Notes" value={form.notes} onChange={e=>setForm(p=>({...p,notes:e.target.value}))} placeholder="Any additional notes..." rows={2} />
          <div style={{display:'flex',gap:10}}>
            <Btn onClick={saveRecord}>Save Record</Btn>
            <Btn variant="secondary" onClick={()=>setEditModal(null)}>Cancel</Btn>
          </div>
        </Modal>
      )}

      <div style={{overflowX:'auto'}}>
        <table style={{width:'100%',borderCollapse:'collapse',minWidth:600}}>
          <thead>
            <tr style={{borderBottom:'2px solid #3a2a12'}}>
              <th style={{color:'#8a7055',fontSize:11,textTransform:'uppercase',letterSpacing:'0.06em',padding:'10px 14px',textAlign:'left',fontWeight:600,background:'#1a1208'}}>Staff Member</th>
              {types.map(t=>(
                <th key={t.id} style={{color:'#8a7055',fontSize:11,textTransform:'uppercase',letterSpacing:'0.05em',padding:'10px 10px',textAlign:'center',fontWeight:600,background:'#1a1208',minWidth:120}}>
                  {t.title.length > 20 ? t.title.slice(0,20)+'…' : t.title}
                  {t.expiry_days ? <div style={{color:'#5a4030',fontSize:10,fontWeight:400,marginTop:2}}>Every {Math.round(t.expiry_days/365)}yr</div> : null}
                </th>
              ))}
              <th style={{color:'#8a7055',fontSize:11,textTransform:'uppercase',letterSpacing:'0.06em',padding:'10px 14px',textAlign:'center',fontWeight:600,background:'#1a1208'}}>Overall</th>
            </tr>
          </thead>
          <tbody>
            {staff.map(u => {
              const overall = getOverallStatus(u.id);
              return (
                <tr key={u.id} style={{borderBottom:'1px solid #2a1f0f',background:selectedUser===u.id?'#1e1508':'#1a1208'}}>
                  <td style={{padding:'12px 14px'}}>
                    <div style={{color:'#f5e6c8',fontWeight:600,fontSize:13}}>{u.name}</div>
                    <div style={{color:'#5a4030',fontSize:11}}>{u.position}</div>
                  </td>
                  {types.map(t => {
                    const rec = getRecord(u.id, t.id);
                    const status = getComplianceStatus(rec, t);
                    const daysLeft = rec?.expiry_date ? Math.floor((new Date(rec.expiry_date)-new Date())/(24*60*60*1000)) : null;
                    return (
                      <td key={t.id} style={{padding:'12px 10px',textAlign:'center'}}>
                        <div onClick={()=>openEdit(u.id,t)} style={{cursor:'pointer',display:'inline-block'}}>
                          <div style={{color:statusColor[status],fontSize:12,fontWeight:700}}>{statusLabel[status]}</div>
                          {daysLeft !== null && status !== 'complete' && <div style={{color:'#5a4030',fontSize:10,marginTop:2}}>{daysLeft<0?'Expired':'Exp in '+daysLeft+'d'}</div>}
                          {rec?.certificate_url && rec.certificate_url !== '#' && <a href={rec.certificate_url} target="_blank" rel="noreferrer" style={{color:'#6366f1',fontSize:10,display:'block',marginTop:2}} onClick={e=>e.stopPropagation()}>View Cert</a>}
                        </div>
                      </td>
                    );
                  })}
                  <td style={{padding:'12px 14px',textAlign:'center'}}>
                    <span style={{background:statusColor[overall]+'22',color:statusColor[overall],border:`1px solid ${statusColor[overall]}44`,borderRadius:20,padding:'3px 10px',fontSize:11,fontWeight:700}}>
                      {overall==='complete'?'✓ OK':overall==='expiring'?'⚠ Soon':overall==='expired'?'✗ Expired':'Incomplete'}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {staff.length === 0 && <p style={{color:'#5a4030',textAlign:'center',padding:40}}>No staff members yet. Add staff in the Users panel.</p>}
    </div>
  );
}
